import { Fragment, useEffect, useMemo, useRef } from "react";
import cx from "classnames";
import { LessonDetailsContent } from "content";
import { useScrollAndHighlight } from "hooks/common/useScrollAndHighlight";
import {
  ButtonExercise,
  ChooseRightWordExercise,
  CompareImageWithDescriptionExercise,
  DescribeGifExercise,
  DescribeImageExercise,
  DescribeVideoExercise,
  EssayExercise,
  InputWordsIntoGapsExercise,
  MakeWordFromLettersExercise,
  MoveWordsIntoGapsExercise,
  NotesExercise,
  RecordAudioExercise,
  SortWordsExercise,
  TestExercise,
  TrueOrFalseExercise,
  WordsComparingExercise,
} from "components/Exercises";
import { Space } from "components/Common/Space";
import { Divider } from "components/Common/Divider";
import { Loader } from "components/Common/Loader";
import { Empty } from "components/Common/Empty";
import { logError } from "utils/notifications";
import { TExercise } from "types/api/exercise";
import {
  EExerciseType,
  TButtonExercise,
  TChooseRightWordFormExercise,
  TCompareImageWithDescriptionExercise,
  TDescribeGifExercise,
  TDescribeImageExercise,
  TDescribeVideoExercise,
  TEssayExercise,
  TInputWordsIntoGapsExercise,
  TMakeWordFromLettersExercise,
  TMoveWordsIntoGapsExercise,
  TNoteExercise,
  TRecordAudioExercise,
  TSortWordsExercise,
  TTestExercise,
  TTrueOrFalseExercise,
  TWordsComparingExercise,
} from "types/app/exercises";
import "./ExercisesPreview.styles.scss";

type TExercisesPreviewProps = {
  /**
   * Chapter ID to which the exercise belongs.
   */
  chapterId: number;
  isHomeworkExercises: boolean;
  /**
   * Array of lesson chapter exercise data.
   * @default []
   */
  exercises?: TExercise[];
  /**
   * The chapter sequence order.
   * @default 1
   */
  chapterOrder?: number;
  /**
   * If `true` component will display loader with animation.
   * @default false
   */
  loading?: boolean;
  /**
   * Definition ID of the exercise to deep-link to (scroll to + highlight),
   * matched against `exercise.exerciseId`.
   * @default null
   */
  deepLinkExerciseId?: number | null;
  /**
   * Seconds into the video the deep-linked VIDEO exercise should start at.
   * Only meaningful together with `deepLinkExerciseId`.
   * @default null
   */
  deepLinkStartSeconds?: number | null;
};

type TExercisePreviewItemProps = {
  isDeepLinkTarget: boolean;
  children: React.ReactNode;
};

const ExercisePreviewItem: React.FC<TExercisePreviewItemProps> = (props) => {
  const { isDeepLinkTarget, children } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const { highlight } = useScrollAndHighlight(ref.current, {
    enable: isDeepLinkTarget,
    highlightTime: 2000,
    scrollOffset: 20,
    delay: 50, // We need to delay for a bit before ref hook assign div element
  });

  return (
    <div
      ref={ref}
      className={cx("nb-exercise-preview-item", {
        "nb-exercise-preview-item--highlight": highlight,
      })}
    >
      {children}
    </div>
  );
};

export const ExercisesPreview: React.FC<TExercisesPreviewProps> = (props) => {
  const {
    chapterId,
    exercises = [],
    chapterOrder = 1,
    loading = false,
    isHomeworkExercises,
    deepLinkExerciseId = null,
    deepLinkStartSeconds = null,
  } = props;

  const isDeepLinkTarget = (exercise: TExercise) =>
    deepLinkExerciseId != null && exercise.exerciseId === deepLinkExerciseId;

  const renderExercise = (
    exercise: TExercise,
    prefix: string,
    isTarget: boolean
  ) => {
    switch (exercise.type) {
      case EExerciseType.ADD_TO_DICTIONARY:
        return null;

      case EExerciseType.BUTTON:
        return (
          <ButtonExercise
            exercise={exercise as TButtonExercise}
            prefix={prefix}
            chapterId={chapterId}
          />
        );

      case EExerciseType.TEST:
        return (
          <TestExercise
            exercise={exercise as TTestExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.CHOOSE_RIGHT_WORD_FORM:
        return (
          <ChooseRightWordExercise
            exercise={exercise as TChooseRightWordFormExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.WORDS_COMPARING:
        return (
          <WordsComparingExercise
            exercise={exercise as TWordsComparingExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.MAKE_WORD_FROM_LETTERS:
        return (
          <MakeWordFromLettersExercise
            exercise={exercise as TMakeWordFromLettersExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.COMPARE_IMAGE_WITH_DESCRIPTION:
        return (
          <CompareImageWithDescriptionExercise
            exercise={exercise as TCompareImageWithDescriptionExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.DESCRIBE_AUDIO:
        return null;

      case EExerciseType.GIF:
        return (
          <DescribeGifExercise
            exercise={exercise as TDescribeGifExercise}
            prefix={prefix}
            chapterId={chapterId}
          />
        );

      case EExerciseType.IMAGE:
        return (
          <DescribeImageExercise
            exercise={exercise as TDescribeImageExercise}
            prefix={prefix}
            chapterId={chapterId}
          />
        );

      case EExerciseType.VIDEO:
        return (
          <DescribeVideoExercise
            exercise={exercise as TDescribeVideoExercise}
            prefix={prefix}
            chapterId={chapterId}
            autoPlayStartSeconds={
              isTarget ? deepLinkStartSeconds ?? 0 : undefined
            }
          />
        );

      case EExerciseType.MOVE_WORDS_INTO_GAPS:
        return (
          <MoveWordsIntoGapsExercise
            exercise={exercise as TMoveWordsIntoGapsExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.TEACHER_NOTES:
        return (
          <NotesExercise
            prefix={prefix}
            chapterId={chapterId}
            exercise={exercise as TNoteExercise}
          />
        );

      case EExerciseType.RECORD_AUDIO:
        return (
          <RecordAudioExercise
            exercise={exercise as TRecordAudioExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.SENTENCE_ORDER:
        return null;

      case EExerciseType.SORT_WORDS:
        return (
          <SortWordsExercise
            exercise={exercise as TSortWordsExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.TRUE_OR_FALSE:
        return (
          <TrueOrFalseExercise
            exercise={exercise as TTrueOrFalseExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.INPUT_WORDS_INTO_GAPS:
        return (
          <InputWordsIntoGapsExercise
            exercise={exercise as TInputWordsIntoGapsExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      case EExerciseType.ESSAY:
        return (
          <EssayExercise
            exercise={exercise as TEssayExercise}
            prefix={prefix}
            chapterId={chapterId}
            isHomeworkExercise={isHomeworkExercises}
          />
        );

      default:
        logError({
          message: LessonDetailsContent.Preview.Error.INVALID_EXERCISE,
        });
        return null;
    }
  };

  const isEmpty: boolean = useMemo(() => exercises.length === 0, [exercises]);

  useEffect(() => {
    if (loading || isEmpty || deepLinkExerciseId == null) return;
    const targetExists = exercises.some(
      (exercise) => exercise.exerciseId === deepLinkExerciseId
    );
    if (!targetExists) {
      logError({
        message: LessonDetailsContent.Preview.Error.INVALID_LESSON_EXERCISE_ID,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isEmpty, exercises, deepLinkExerciseId]);

  return (
    <Space direction="vertical" justify="start" size="medium" fullWidth>
      {loading && <Loader />}
      {isEmpty && !loading && (
        <Empty text={LessonDetailsContent.Preview.Empty.TEXT} />
      )}
      {!isEmpty &&
        !loading &&
        exercises.map((exercise, index) => (
          <Fragment key={exercise.id}>
            <ExercisePreviewItem isDeepLinkTarget={isDeepLinkTarget(exercise)}>
              {renderExercise(
                exercise,
                `${chapterOrder}.${index + 1}.`,
                isDeepLinkTarget(exercise)
              )}
            </ExercisePreviewItem>
            {index !== exercises.length - 1 && <Divider />}
          </Fragment>
        ))}
    </Space>
  );
};
